import React, { useState, useEffect } from 'react';
import { LoremIpsumService, LoremType } from '../../lib/services/loremIpsumService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType } from '../../lib/types';
import './LoremIpsum.css';

export const LoremIpsum: React.FC = () => {
  const [output, setOutput] = useState('');
  const [quantity, setQuantity] = useState(3);
  const [type, setType] = useState<LoremType>('paragraphs');
  const [startWithLorem, setStartWithLorem] = useState(true);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await LoremIpsumService.generate(quantity, type, startWithLorem);
      setOutput(result.outputData as string);
      setError(null);
    } catch (err) {
      const errorMessage = ErrorHandler.handle(err, EncoderToolType.LOREM_IPSUM);
      setError(errorMessage);
      setOutput('');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGenerate();
  }, [quantity, type, startWithLorem]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Text';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!output) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  return (
    <div className="lorem-ipsum">
      <div style={{ marginBottom: '2rem' }}>
        <h1>Lorem Ipsum Generator</h1>
        <p>
          Generate placeholder Lorem Ipsum text for your designs and mockups.
          Choose between paragraphs, sentences, or words.
        </p>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label>
            Type:
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as LoremType)}
            data-testid="type-select"
          >
            <option value="paragraphs">Paragraphs</option>
            <option value="sentences">Sentences</option>
            <option value="words">Words</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            Quantity:
          </label>
          <input
            type="number"
            min="1"
            max="100"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
            data-testid="quantity-input"
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={startWithLorem}
              onChange={(e) => setStartWithLorem(e.target.checked)}
              data-testid="start-with-lorem-checkbox"
            />
            Start with "Lorem ipsum"
          </label>
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        <div className="form-group">
          <label>
            Generated Text:
          </label>
          <textarea
            value={output}
            readOnly
            placeholder={isLoading ? 'Generating...' : 'Generated text will appear here...'}
            className={isLoading ? 'loading' : ''}
            data-testid="output-text"
            style={{ height: '300px' }}
          />
          {isLoading && (
            <div className="loading-indicator">Generating...</div>
          )}
        </div>

        <div className="button-container">
          <button
            onClick={handleCopy}
            disabled={!output}
            className={getButtonClassName()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};
